import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from 'config/axiosInstance';
import { fetchAll } from 'config/fetchAll';
import { businessDateRange, toBusinessDate } from 'config/time';
import ClientInfo from './ClientInfo';
import TaskAssignment from './TaskAssignment';
import DriverAssignment from './DriverAssignment';
import {
  useBeforeUnload,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { CLIENT_PATH } from 'router/pathName';
import { Task } from '../../../reusable/Client/dialog/AddTaskDialog';
import {
  secondsToHoursAndMinutes,
  timeStringToSeconds,
} from '../Utility/timeHelpers';
import DiscardChangesDialog from '../../../reusable/Client/dialog/DiscardChangesDialog';
import SaveChangesDialog from '../../../reusable/Client/dialog/SaveChangesDialog';
import { useTranslation } from 'react-i18next';
import { useSaveError } from 'reusable/useSaveError';
import FetchFailed from 'reusable/FetchFailed';

interface Client {
  name: string;
  description: string;
  tasks: number[];
  assign_drivers: number[];
}

/**
 * EV-137。保存した内容と比べるための形。作業は並べ替えも保存の対象なので順も比べ、
 * 運転者は集合として比べる。
 */
interface Snapshot {
  name: string;
  description: string;
  tasks: number[];
  drivers: number[];
}

const snapshotOf = (
  name: string,
  description: string,
  tasks: number[],
  drivers: number[]
): Snapshot => ({
  name,
  description,
  tasks: [...tasks],
  drivers: [...drivers].sort((a, b) => a - b),
});

const sameIds = (a: number[], b: number[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const sameSnapshot = (a: Snapshot, b: Snapshot) =>
  a.name === b.name &&
  a.description === b.description &&
  sameIds(a.tasks, b.tasks) &&
  sameIds(a.drivers, b.drivers);

/**
 * RV-246。ブラウザの戻るを使ったとき、この時間のうちに画面を離れなければ（戻る先が進む方向に
 * しか無かったなど）配送先の一覧へ移る。
 */
const LEAVE_FALLBACK_MS = 500;

/** ADM-08。当年の1月1日〜12月31日を業務日で表す。 */
const currentYearRange = () => {
  const year = toBusinessDate(new Date()).slice(0, 4);
  return businessDateRange(`${year}-01-01`, `${year}-12-31`);
};

const EditClient: React.FC = () => {
  const { t } = useTranslation('client');
  const { showSaveError, saveErrorPopup } = useSaveError();
  const [client, setClient] = useState<Client>({
    name: '',
    description: '',
    tasks: [],
    assign_drivers: [],
  });

  const [totalDurationWithTasks, setTotalDurationWithTasks] = useState<{
    hours: number;
    minutes: number;
  } | null>(null);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedDrivers, setSelectedDrivers] = useState<number[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  // const [isRemoveTaskPopupOpen, setIsRemoveTaskPopupOpen] = useState(false);
  // const [isSaveChangesPopupOpen, setIsSaveChangesPopupOpen] = useState(false);
  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] =
    useState(false);
  const [isSaveChangesDialogOpen, setIsSaveChangesDialogOpen] = useState(false);
  const [selectedTaskIdForRemoval, setSelectedTaskIdForRemoval] = useState<
    number | null
  >(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [showErrorTooltip, setShowErrorTooltip] = useState(false);
  // ADM-15。取得に失敗したら、空の入力欄を出さない（保存すると名前・割当が空で上書きされ得る）。
  const [fetchError, setFetchError] = useState<unknown>(null);
  // EV-137。読み込んだ（保存の成功の後は送った）内容。読み込むまでは null で、確認しない。
  const [savedSnapshot, setSavedSnapshot] = useState<Snapshot | null>(null);
  // 「破棄」のボタンで確認した後の移動は、もう一度止めない。
  const leavingRef = useRef(false);
  // RV-246。ブラウザの戻るで離れなかったときに一覧へ移す待ち。
  const leaveFallbackRef = useRef<number | null>(null);

  // Fetch client and related data
  const fetchData = useCallback(async () => {
    // RV-243。別の配送先を読み込む間は、前の配送先の内容と比べない（確認しない）。
    setSavedSnapshot(null);
    try {
      const response = await axiosInstance.get(`/v1/client_deliveries/${id}/`);
      const clientData: Omit<Client, 'description'> & { description?: string | null } = response.data;
      setClient({ ...clientData, description: clientData.description ?? '' }); // EV-298。説明の null・無しは空として持つ。
      setSelectedDrivers(clientData.assign_drivers || []);

      // Fetch all tasks and drivers to populate the lists
      const [tasksResponse, driversResponse, totalDurationResponsewithTask] =
        await Promise.all([
          // BE-21。page_size=0では全件が返らないため、ページを順に読む。
          fetchAll<Task>('/v1/tasks/'),
          fetchAll<any>('/v1/drivers/'),
          axiosInstance.get(
            `/v1/client_deliveries/summarize_spent_hour_with_tasks/?id__in=${id}` +
          // ADM-08。2024年固定ではなく、要求時点の年を対象にする。
          `&start_time=${encodeURIComponent(currentYearRange().gte ?? '')}` +
          `&stop_time=${encodeURIComponent(currentYearRange().lte ?? '')}`
          ),
        ]);

      const fetchedTasks = tasksResponse;
      const fetchedDrivers = driversResponse;

      // Set tasks and drivers in state
      setTasks(fetchedTasks);
      setDrivers(fetchedDrivers);

      // Map over clientData.tasks to find corresponding task details
      const selectedTasksWithDetails = clientData.tasks.map(
        (taskId: number) => {
          const taskDetails = fetchedTasks.find(
            (task: Task) => task.id === taskId
          );
          return {
            id: taskId,
            name: taskDetails ? taskDetails.name : '',
            icon: taskDetails ? taskDetails.icon : 'default',
            isDisabled: true,
          };
        }
      );

      // Update selectedTasks with the correct task details
      setSelectedTasks(selectedTasksWithDetails);
      // EV-137。画面の作業・運転者をそろえた後で、読み込んだ内容を保存済みとする（一覧を待つ間は
      // 作業が空のため、先に記録すると変更が無いのに確認が出た。独立照合の指摘）。
      setSavedSnapshot(
        snapshotOf(
          clientData.name,
          clientData.description ?? '',
          clientData.tasks || [],
          clientData.assign_drivers || []
        )
      );

      // Calculate total duration with tasks
      const hourSpentArray =
        totalDurationResponsewithTask.data[0]?.summarize || [];
      const totalSeconds = hourSpentArray.reduce(
        (total: number, entry: any) => {
          return total + timeStringToSeconds(entry.hour_spent);
        },
        0
      );

      // EV-115。0時間も値として持つ（null は読み込み中・取得できないときだけ）。
      setTotalDurationWithTasks(secondsToHoursAndMinutes(totalSeconds));
      setFetchError(null);
    } catch (error) {
      console.error('Failed to fetch data', error);
      setFetchError(error);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // EV-137。保存していない変更があるか。読み込みの失敗の表示では入力欄が無いので確認しない。
  const isDirty =
    savedSnapshot !== null &&
    !fetchError &&
    !sameSnapshot(
      savedSnapshot,
      snapshotOf(
        client.name,
        client.description,
        selectedTasks.map((task) => task.id),
        selectedDrivers
      )
    );
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // RV-250。読み込みが全部終わる（保存した内容を記録する）まで、編集・保存をさせない。
  // 一覧を待つ間の編集は比べる元が無く確認されず、作業の追加は一覧がそろったときに上書きされた。
  // 追加画面の送信中（RV-129）と同じく、fieldset と各部品の disabled で止め、それを通らない経路も
  // ここで捨てる。
  const hydrating = savedSnapshot === null && !fetchError;
  const hydratingRef = useRef(hydrating);
  hydratingRef.current = hydrating;
  const whenEditable =
    <A extends unknown[]>(update: (...args: A) => void) =>
    (...args: A) => {
      if (!hydratingRef.current) update(...args);
    };

  // 画面内の移動（リンク・戻る・進む）は止めて「変更を破棄」の確認を出す。
  const blocker = useBlocker(
    useCallback(() => isDirtyRef.current && !leavingRef.current, [])
  );
  // タブを閉じる・再読込・アドレス欄での移動は、ブラウザ標準の確認を出す。
  useBeforeUnload(
    useCallback((event: BeforeUnloadEvent) => {
      if (!isDirtyRef.current || leavingRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    }, [])
  );
  const isLeaveBlocked = blocker.state === 'blocked';

  /** RV-246。ブラウザの戻るで離れなかったときに一覧へ移す予備のタイマーを取り消す。 */
  const clearLeaveFallback = useCallback(() => {
    if (leaveFallbackRef.current !== null) {
      window.clearTimeout(leaveFallbackRef.current);
      leaveFallbackRef.current = null;
    }
  }, []);

  // RV-243。「破棄」で確かめた後の素通しは、その1回の移動に限る。同じ部品のまま別の配送先の
  // 編集へ移ったら、確認をまた効かせる。RV-253。戻れた（画面の位置が変わった）ら、一覧へ移す
  // 予備のタイマーも取り消す（同じ経路へ戻ると部品が使い回され、外したときの片付けが動かない）。
  // React Router の状態を持たない項目どうし（ページ全体の読み込みで来た）は key がどちらも
  // default なので、key ではなく移動ごとに新しくなる location の値で見る。
  useEffect(() => {
    leavingRef.current = false;
    clearLeaveFallback();
  }, [location, clearLeaveFallback]);

  // RV-246。ブラウザの戻るで離れる途中（pagehide）なら一覧へ移さない。戻る・進むのキャッシュから
  // 戻ってきた（pageshow）ら、確認をまた効かせる。
  useEffect(() => {
    const clearFallback = clearLeaveFallback;
    const onPageShow = (event: Event) => {
      if ((event as PageTransitionEvent).persisted) leavingRef.current = false;
    };
    window.addEventListener('pagehide', clearFallback);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      clearFallback();
      window.removeEventListener('pagehide', clearFallback);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [clearLeaveFallback]);

  /**
   * 「破棄」で確かめた後に、来た画面へ戻る。RV-246。戻る先があるかは location.key ではなく、
   * React Router が history.state に入れる idx（アプリの中の位置）と、ブラウザの履歴の数で見る。
   */
  const leaveBack = () => {
    leavingRef.current = true;
    const idx = window.history.state?.idx;
    if (typeof idx !== 'number') {
      // ブラウザの履歴を使わない router（試験の memory router）。最初の項目かを key で見る。
      if (location.key === 'default') {
        navigate(CLIENT_PATH);
      } else {
        navigate(-1);
      }
      return;
    }
    if (idx > 0) {
      navigate(-1);
      return;
    }
    if (window.history.length > 1) {
      // アプリの中では最初の項目だが、ブラウザには前の項目がある（ページ全体の読み込みで来た）。
      leaveFallbackRef.current = window.setTimeout(() => {
        leaveFallbackRef.current = null;
        navigate(CLIENT_PATH);
      }, LEAVE_FALLBACK_MS);
      window.history.back();
      return;
    }
    navigate(CLIENT_PATH);
  };

  // Handle form submission to update the client
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (hydratingRef.current) return;
    if (selectedTasks.length === 0) {
      setShowErrorTooltip(true);
      return;
    }
    try {
      const updatedClient = {
        name: client.name,
        description: client.description,
        assign_drivers: selectedDrivers,
        tasks: selectedTasks.map((task) => task.id),
      };

      const response = await axiosInstance.patch(
        `/v1/client_deliveries/${id}/`,
        updatedClient
      );
      // EV-137。作業数・割当人数は保存した内容で表示する（応答に無ければ送った値）。
      const savedData = response?.data ?? {};
      setClient((prevClient) => ({
        ...prevClient,
        tasks: Array.isArray(savedData.tasks)
          ? savedData.tasks
          : updatedClient.tasks,
        assign_drivers: Array.isArray(savedData.assign_drivers)
          ? savedData.assign_drivers
          : updatedClient.assign_drivers,
      }));
      // 送った内容を保存済みとし、さらに変えるまで確認しない。
      setSavedSnapshot(
        snapshotOf(
          updatedClient.name,
          updatedClient.description,
          updatedClient.tasks,
          updatedClient.assign_drivers
        )
      );
      setIsSaveChangesDialogOpen(true);
    } catch (error) {
      console.error('Failed to update client', error);
      showSaveError(error);
    }
  };

  if (fetchError) {
    return (
      <section className="bg-white rounded-md m-4">
        <FetchFailed
          error={fetchError}
          onRetry={fetchData}
          message={t('ui.detail-fetch-failed', { ns: 'common' })}
        />
      </section>
    );
  }

  return (
    <section className="bg-white rounded-md m-4">
      <form onSubmit={handleSubmit}>
        {/* RV-250。読み込みが全部終わるまで、入力・作業・運転者の操作をまとめて無効にする。 */}
        <fieldset disabled={hydrating} aria-busy={hydrating} className="min-w-0">
        <ClientInfo
          clientName={client.name}
          setClientName={whenEditable((name: string) =>
            setClient((prevClient) => ({
              ...prevClient,
              name,
            }))
          )}
          clientDescription={client.description}
          setClientDescription={whenEditable((description: string) =>
            setClient((prevClient) => ({
              ...prevClient,
              description,
            }))
          )}
          totalDurationWithTasks={totalDurationWithTasks}
          activeTasks={client.tasks.length}
          driversAssigned={client.assign_drivers.length}
        />
        <hr />
        <TaskAssignment
          clientName={client.name}
          selectedTasks={selectedTasks}
          setSelectedTasks={whenEditable(setSelectedTasks)}
          showErrorTooltip={showErrorTooltip}
          setShowErrorTooltip={() => setShowErrorTooltip(false)}
          isRemoveTask={true}
          disabled={hydrating}
        />
        <hr />

        <DriverAssignment
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          drivers={drivers}
          selectedDrivers={selectedDrivers}
          handleDriverSelect={whenEditable((driver) =>
            setSelectedDrivers((prev) => [...prev, driver.id])
          )}
          handleDriverRemove={whenEditable((driverId: number) =>
            setSelectedDrivers((prev) => prev.filter((id) => id !== driverId))
          )}
          disabled={hydrating}
        />
        </fieldset>
        <div className="flex justify-end mt-4 gap-4 border-t p-4">
          <button
            type="button"
            className="bg-gray-200 font-semi-bold text-subtitle-2 text-primary/300 h-12 py-2 px-20 rounded-md"
            onClick={() => setIsDiscardChangesDialogOpen(true)}
          >
            {t('action.discard')}
          </button>
          <button
            type="submit"
            disabled={hydrating}
            aria-busy={hydrating}
            className="bg-primary/300 font-medium text-subtitle-2 text-white h-12 py-2 px-12 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('action.save_changes')}
          </button>
        </div>
      </form>

      <DiscardChangesDialog
        isOpen={isDiscardChangesDialogOpen || isLeaveBlocked}
        onCancel={() => {
          setIsDiscardChangesDialogOpen(false);
          if (isLeaveBlocked) blocker.reset?.();
        }}
        onDiscard={() => {
          setIsDiscardChangesDialogOpen(false);
          if (isLeaveBlocked) {
            blocker.proceed?.();
            return;
          }
          // 戻り先の履歴が無い（新しいタブで直接開いた）ときは配送先の一覧へ移る（独立照合の指摘・
          // RV-245）。判定は leaveBack（RV-246）。
          leaveBack();
        }}
      />

      <SaveChangesDialog
        isOpen={isSaveChangesDialogOpen}
        onClose={() => setIsSaveChangesDialogOpen(false)}
      />
      {saveErrorPopup}
    </section>
  );
};

export default EditClient;
